import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Calculator,
  BookOpen,
  Briefcase,
  FileSignature,
  Plane,
  KeyRound,
  ArrowUpRight,
  Sparkles,
  Heart,
  Quote,
  CheckCircle2,
  Star,
  Download,
  FileText,
} from "lucide-react";
import { motion } from "framer-motion";
import Newsletter from "@/components/Newsletter";
import JsonLd from "@/components/JsonLd";
import useSeo from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { TESTIMONIALS } from "@/lib/data";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

// Curated home-page service groupings (per the brand template).
// Underlying detail pages still live at /services/<slug> for the 11 granular services.
const HOME_SERVICES = [
  {
    slug: "credit-repair",
    icon: ShieldCheck,
    color: "bg-gold",
    title: "Anew Credit Repair",
    text:
      "Helping individuals rebuild, restore, and strengthen their credit through education, strategic planning, and personalized support.",
    items: [
      "Credit Analysis",
      "Credit Restoration",
      "Bankruptcy Recovery",
      "Credit Education",
      "Budgeting Guidance",
    ],
  },
  {
    slug: "taxes",
    icon: Calculator,
    color: "bg-navy",
    title: "Divinity Taxes",
    text:
      "Professional tax preparation for individuals, families, self-employed professionals, and small businesses.",
    items: [
      "Individual Tax Preparation",
      "Self-Employed Tax Filing",
      "Business Tax Returns",
      "Prior-Year Returns",
      "Tax Planning",
    ],
  },
  {
    slug: "bookkeeping",
    icon: BookOpen,
    color: "bg-teal",
    title: "ConnectHER Bookkeeping",
    text:
      "Helping businesses maintain organized financial records and stronger financial systems through accurate bookkeeping and reporting.",
    items: [
      "QuickBooks Setup",
      "Monthly Bookkeeping",
      "Cleanup Services",
      "Financial Reports",
      "Bill.com Support",
    ],
  },
  {
    slug: "consulting",
    icon: Briefcase,
    color: "bg-navy",
    title: "Full Circle Consulting",
    text:
      "Supporting entrepreneurs and organizations with business formation, consulting, and administrative support services.",
    items: [
      "LLC Formation",
      "EIN Assistance",
      "Business Consulting",
      "Administrative Support",
      "Compliance Guidance",
    ],
  },
  {
    slug: "notary",
    icon: FileSignature,
    color: "bg-gold",
    title: "Notary & Signing Services",
    text:
      "Convenient, confidential notary and loan-signing services for individuals, families, and businesses.",
    items: [
      "General Notary Services",
      "Mobile Notary Services",
      "Loan Signing Agent Services",
      "Document Witnessing",
    ],
  },
  {
    slug: "travel",
    icon: Plane,
    color: "bg-teal",
    title: "ConnectHER Travel",
    text:
      "Curated travel experiences booked through Fora's luxury partner network — hotels, resorts, cruises, honeymoons, family reunions, and corporate retreats. Most planning is complimentary.",
    items: [
      "Hotels, Resorts & Cruises",
      "Honeymoons & Celebrations",
      "Family Reunions & Group Travel",
      "Corporate Retreats",
      "Custom Itineraries",
    ],
  },
  {
    slug: "real-estate",
    icon: KeyRound,
    color: "bg-navy",
    title: "Real Estate — Journey to Keys™",
    text:
      "From rent-ready to home-ready. A guided path to homeownership combining credit, savings, and homebuyer education. Join the early-access list.",
    items: [],
    comingSoon: true,
  },
];

function TrustStrip() {
  // Real partners & platforms ConnectHER works with — fully replaceable with logos
  // once the user provides them. For now, displayed as elegant text marks.
  const partners = [
    { name: "Fora Travel", note: "Travel Advisor" },
    { name: "EPS Financial", note: "Tax Banking" },
    { name: "Drake Tax", note: "Tax Software" },
    { name: "SmartClerk", note: "Expense Extraction" },
    { name: "Bookipi", note: "Billing" },
    { name: "Stripe", note: "Payments" },
    { name: "QuickBooks", note: "Bookkeeping" },
    { name: "Bill.com", note: "AP" },
    { name: "Acuity", note: "Scheduling" },
    { name: "SmartCredit", note: "Credit Monitoring" },
    { name: "FreedomPath", note: "Business Credit" },
    { name: "Mailchimp", note: "Email" },
    { name: "HubSpot", note: "CRM" },
  ];
  return (
    <section className="py-12 md:py-16 border-y border-navy/5 bg-white" data-testid="home-trust-strip">
      <div className="container-app">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-navy/45">
            Trusted partners &amp; platforms we use
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-5 md:gap-x-12">
          {partners.map((p) => (
            <div
              key={p.name}
              className="flex flex-col items-center text-center opacity-60 hover:opacity-100 transition"
              data-testid={`trust-${p.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            >
              <div className="font-serif text-xl md:text-2xl text-navy leading-none">
                {p.name}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-navy/40">
                {p.note}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden pt-12 md:pt-20 pb-20 md:pb-28"
    >
      <div className="blob bg-gold w-72 h-72 -top-12 -left-20 rounded-full" />
      <div className="blob bg-teal/40 w-96 h-96 top-40 -right-24 rounded-full" />
      <div className="container-app relative grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-6 z-10">
          <motion.div {...fadeUp} className="eyebrow mb-5">
            <Sparkles className="inline w-3.5 h-3.5 mr-2 -mt-0.5" />
            The Divine Connector
          </motion.div>
          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.08 }}
            className="text-5xl sm:text-6xl lg:text-7xl leading-[1.05]"
          >
            Connecting people, businesses, and communities to the{" "}
            <span className="italic text-gold">support they need.</span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-6 text-lg md:text-xl text-navy/70 leading-relaxed max-w-xl"
          >
            ConnectHER is a business and financial support ecosystem helping
            individuals, families, nonprofits, and small businesses build
            stronger financial futures — through credit solutions, tax
            services, bookkeeping, business support, and notary services.
          </motion.p>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.22 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Link to="/book" data-testid="hero-book-cta" className="btn-primary">
              Book a Consultation <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/services" data-testid="hero-services-cta" className="btn-secondary">
              Explore Services
            </Link>
            <Link to="/help-me-choose" data-testid="hero-quiz-cta" className="btn-teal">
              <Sparkles className="w-4 h-4" /> Help Me Choose
            </Link>
          </motion.div>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.3 }}
            className="mt-10 flex items-center gap-6 text-sm text-navy/60"
          >
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-gold" /> Trusted nationwide
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-teal" /> Confidential &amp; secure
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="md:col-span-6 relative"
        >
          <div className="relative rounded-[2.5rem] overflow-hidden shadow-cardHover bg-navy/5">
            <img
              src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=1400&q=80"
              alt="ConnectHer team of professional women"
              className="w-full h-[520px] object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-navy/40 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-card p-5 max-w-[230px] hidden md:block">
            <div className="text-xs uppercase tracking-widest text-gold">
              One ecosystem
            </div>
            <div className="font-serif text-lg text-navy leading-tight mt-1">
              Multiple services. One trusted partner.
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Welcome() {
  return (
    <section className="section bg-white" data-testid="home-welcome">
      <div className="container-app grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <div className="eyebrow">Welcome</div>
          <h2 className="mt-3 text-4xl md:text-5xl">
            Welcome to{" "}
            <span className="italic text-gold">ConnectHER.</span>
          </h2>
        </div>
        <div className="md:col-span-7 text-navy/75 leading-relaxed space-y-5 text-lg">
          <p>
            At ConnectHER, we believe access to the right support can change
            everything. Our mission is to help individuals, families,
            nonprofits, and small business owners navigate financial,
            business, and administrative challenges with confidence.
          </p>
          <p>
            Through our connected network of services, we provide practical
            solutions, education, and guidance designed to help our clients
            move forward.
          </p>
          <p>
            Whether you&apos;re rebuilding your credit, filing taxes, organizing
            your bookkeeping, starting a business, or seeking professional
            support — ConnectHER is here to help.
          </p>
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  const points = [
    {
      title: "One trusted ecosystem",
      body:
        "Instead of sending clients from one company to another, we bring multiple professional services together under one roof.",
    },
    {
      title: "Personalized support",
      body:
        "We focus on relationships first. Every plan is built around your specific situation, goals, and timeline.",
    },
    {
      title: "Built for long-term success",
      body:
        "Education, organization, and accountability are baked into every engagement — so the wins stick.",
    },
  ];
  return (
    <section className="section" data-testid="home-why-choose">
      <div className="container-app grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <div className="eyebrow">Why choose us</div>
          <h2 className="mt-3 text-4xl md:text-5xl">
            Why choose{" "}
            <span className="italic text-teal">ConnectHER?</span>
          </h2>
          <p className="mt-6 text-navy/70 leading-relaxed">
            Life and business rarely require just one solution. ConnectHER
            exists so you don&apos;t have to coordinate five different vendors to
            move forward.
          </p>
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-3 gap-4">
          {points.map((p, i) => (
            <motion.div
              key={p.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="card p-6"
            >
              <div className="text-3xl font-serif text-gold">0{i + 1}</div>
              <h4 className="mt-3 text-xl">{p.title}</h4>
              <p className="mt-2 text-sm text-navy/65 leading-relaxed">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesShowcase() {
  return (
    <section className="section bg-white" data-testid="home-services-showcase">
      <div className="container-app">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div className="max-w-xl">
            <div className="eyebrow">Our services</div>
            <h2 className="mt-3 text-4xl md:text-5xl">
              Designed to support financial wellness,{" "}
              <span className="italic text-gold">business growth</span>, and
              personal success.
            </h2>
            <p className="mt-5 text-navy/70 leading-relaxed">
              Explore our service divisions below — each stands on its own and
              stacks beautifully when combined.
            </p>
          </div>
          <Link
            to="/services"
            className="btn-secondary"
            data-testid="services-cta-secondary"
          >
            View All Services <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {HOME_SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isComingSoon = !!s.comingSoon;
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-card-${s.slug}`}
                  className="card block p-7 h-full group relative"
                >
                  {isComingSoon && (
                    <div className="absolute top-5 right-5 bg-gold/15 text-gold text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full">
                      Coming Soon
                    </div>
                  )}
                  <div
                    className={`w-12 h-12 rounded-2xl ${s.color} text-white flex items-center justify-center mb-5`}
                  >
                    <Icon className="w-6 h-6" strokeWidth={1.8} />
                  </div>
                  <h3 className="text-2xl mb-2">{s.title}</h3>
                  <p className="text-navy/65 text-sm leading-relaxed mb-5">
                    {s.text}
                  </p>
                  {s.items.length > 0 && (
                    <ul className="space-y-2 mb-5">
                      {s.items.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 text-sm text-navy/70"
                        >
                          <CheckCircle2 className="w-4 h-4 text-teal mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex items-center justify-between pt-4 border-t border-navy/5">
                    <span className="text-xs uppercase tracking-widest text-gold">
                      {isComingSoon ? "Coming Soon" : "Learn More"}
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-navy/40 group-hover:text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FounderMessage() {
  return (
    <section className="section" data-testid="home-founder">
      <div className="container-app">
        <div className="rounded-[2.5rem] bg-soft border border-navy/5 p-10 md:p-16 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-4 flex justify-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gold/20 rounded-full blur-2xl" />
              <div className="relative w-48 h-48 md:w-60 md:h-60 rounded-full overflow-hidden border-4 border-white shadow-cardHover">
                <img
                  src="https://customer-assets.emergentagent.com/job_upgrade-web-4/artifacts/1vz8xcvl_IMG_20260624_150155.jpg"
                  alt="Devyne Harris, founder of ConnectHER"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="eyebrow">A message from our founder</div>
            <Quote className="w-9 h-9 text-gold mt-4" />
            <p className="mt-4 text-lg md:text-xl text-navy/80 leading-relaxed font-serif italic">
              ConnectHER was built from a desire to help people access the
              support, education, and resources they need to succeed. Having
              personally navigated financial challenges, entrepreneurship, and
              the realities of balancing business and family, I understand the
              importance of having trusted guidance along the way.
            </p>
            <p className="mt-4 text-navy/70 leading-relaxed">
              My goal is to create a space where clients feel supported,
              informed, and empowered to take their next step with confidence.
            </p>
            <div className="mt-6 pt-6 border-t border-navy/10">
              <div className="font-serif text-2xl text-navy">— Devyne Harris</div>
              <div className="text-sm uppercase tracking-widest text-gold mt-1">
                Founder, ConnectHER
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    {
      n: "01",
      title: "Reach out",
      body:
        "Book a complimentary 15-minute consultation. Tell us what's on your mind — no pressure, no judgment.",
    },
    {
      n: "02",
      title: "Get a plan",
      body:
        "We map the right service or bundle to your goals, scope it transparently, and send you a clear proposal.",
    },
    {
      n: "03",
      title: "Move forward",
      body:
        "Work alongside a team that handles the heavy lifting while teaching you the systems along the way.",
    },
  ];
  return (
    <section className="section" data-testid="home-process">
      <div className="container-app">
        <div className="max-w-2xl mb-12">
          <div className="eyebrow">How it works</div>
          <h2 className="mt-3 text-4xl md:text-5xl">
            Three simple steps to{" "}
            <span className="italic text-gold">moving forward.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 relative">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="card p-7 relative"
            >
              <div className="font-serif text-5xl text-gold/30 leading-none">
                {s.n}
              </div>
              <h4 className="mt-4 text-2xl">{s.title}</h4>
              <p className="mt-3 text-sm text-navy/65 leading-relaxed">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}


function CTA() {
  return (
    <section className="section" data-testid="home-cta-section">
      <div className="container-app">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-navy via-navy to-teal p-10 md:p-16 text-soft">          <div className="blob bg-gold w-72 h-72 -bottom-20 -right-10 rounded-full opacity-30" />
          <div className="relative max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-gold mb-4">
              Ready to get started?
            </div>
            <h2 className="text-4xl md:text-5xl text-soft">
              Let&apos;s build your next step{" "}
              <span className="italic text-gold">together.</span>
            </h2>
            <p className="mt-5 text-soft/80 leading-relaxed text-lg">
              Whether you need help with credit, taxes, bookkeeping, business
              services, or notary support, we&apos;re here to help you find the
              right solution. Schedule a consultation today.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/book"
                data-testid="home-cta-book"
                className="btn-primary"
              >
                Book Your Consultation <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/intake"
                data-testid="home-cta-intake"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-soft text-soft hover:bg-soft hover:text-navy transition-colors rounded-full px-7 py-3 text-sm font-medium uppercase tracking-wider"
              >
                Start Intake
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-8 items-center bg-white rounded-3xl border border-navy/5 p-8">
          <div>
            <div className="eyebrow">Stay in the loop</div>
            <h3 className="mt-2 text-2xl md:text-3xl">
              Monthly insights, exclusive offers, &amp; ConnectHER events.
            </h3>
          </div>
          <Newsletter source="home-cta" />
        </div>
      </div>
    </section>
  );
}

function Testimonials({ reviews }) {
  // Prefer real reviews; fall back to seed testimonials so the section is never empty
  const items =
    reviews && reviews.length > 0
      ? reviews.slice(0, 6)
      : TESTIMONIALS.map((t, i) => ({
          author: t.name,
          location: t.role,
          rating: 5,
          body: t.quote,
          _seed: true,
          _key: `seed-${i}`,
        }));

  return (
    <section className="section bg-white" data-testid="home-testimonials">
      <div className="container-app">
        <div className="max-w-2xl mb-12">
          <div className="eyebrow">Client stories</div>
          <h2 className="mt-3 text-4xl md:text-5xl">
            Real wins from{" "}
            <span className="italic text-gold">real people.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((r, i) => (
            <motion.div
              key={r.review_id || r._key || i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="card p-6"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <Star
                    key={idx}
                    className={`w-4 h-4 ${
                      idx < (r.rating || 5)
                        ? "text-gold fill-current"
                        : "text-navy/15"
                    }`}
                  />
                ))}
              </div>
              <Quote className="w-5 h-5 text-gold/40 mb-2" />
              <p className="text-navy/80 italic leading-relaxed text-sm">
                &ldquo;{r.body}&rdquo;
              </p>
              <div className="mt-5 pt-4 border-t border-navy/5">
                <div className="font-serif text-base text-navy">{r.author}</div>
                {r.location && (
                  <div className="text-xs text-navy/55">{r.location}</div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LeadMagnet() {
  return (
    <section className="section" data-testid="home-leadmagnet">
      <div className="container-app">
        <div className="rounded-[2.5rem] overflow-hidden grid md:grid-cols-2 bg-soft border border-navy/5">
          <div className="p-10 md:p-14">
            <div className="eyebrow flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Free Guide
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl leading-tight">
              The ConnectHER{" "}
              <span className="italic text-gold">
                Financial Wellness Starter Kit.
              </span>
            </h2>
            <p className="mt-5 text-navy/70 leading-relaxed">
              5 steps to clarity, control, and confidence with your money — the
              same framework Devyne walks through with every new client on
              their Discovery Call. Drop your email and we&apos;ll send it
              over (plus our monthly money + business insights).
            </p>
            <ul className="mt-6 space-y-2 text-sm text-navy/75">
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-teal mt-0.5 flex-shrink-0" />
                Know your three core numbers in under 10 minutes
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-teal mt-0.5 flex-shrink-0" />
                The "two-account trick" that cuts financial anxiety in half
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-teal mt-0.5 flex-shrink-0" />
                A decision tree for picking your next ConnectHER service
              </li>
            </ul>
            <div className="mt-8 max-w-md">
              <Newsletter
                source="lead-magnet"
                leadMagnet={{
                  url: "/downloads/connecther-starter-kit.html",
                  label: "Download the Starter Kit",
                }}
              />
            </div>
          </div>
          <div className="relative bg-navy text-soft p-10 md:p-14 flex items-center justify-center">
            <div className="absolute inset-0 opacity-25 bg-gradient-to-br from-gold via-transparent to-teal" />
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gold/15 mb-6">
                <FileText className="w-10 h-10 text-gold" />
              </div>
              <div className="font-serif text-2xl text-soft mb-2">
                Financial Wellness Starter Kit
              </div>
              <div className="text-soft/70 text-sm leading-relaxed max-w-xs mx-auto">
                A 5-step PDF guide from Devyne Harris, Founder of ConnectHER.
                Yours free.
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-xs text-gold uppercase tracking-widest">
                <Download className="w-3.5 h-3.5" /> Instant Download
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


export default function HomePage() {
  useSeo({
    title: "Connecting People, Businesses & Communities to the Support They Need",
    description:
      "ConnectHER is a business and financial support ecosystem — credit solutions, tax services, bookkeeping, business support, and notary services for individuals, families, nonprofits, and small businesses.",
    path: "/",
  });

  const [reviews, setReviews] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  useEffect(() => {
    api
      .get("/reviews")
      .then((r) => {
        setReviews(r.data.reviews || []);
        setAggregate(r.data.aggregate || null);
      })
      .catch(() => {});
  }, []);

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
    "@id": "https://thechhq.com/#organization",
    name: "ConnectHer HQ",
    legalName: "ConnectHer HQ",
    alternateName: ["The Divine Connector", "ConnectHer"],
    url: "https://thechhq.com",
    logo: "https://customer-assets.emergentagent.com/job_upgrade-web-4/artifacts/qbbdrrns_FFDD0762-32FF-400B-8794-0C2705294CCC.png",
    image: "https://customer-assets.emergentagent.com/job_upgrade-web-4/artifacts/qbbdrrns_FFDD0762-32FF-400B-8794-0C2705294CCC.png",
    priceRange: "$$",
    description:
      "A business and financial support ecosystem helping individuals, families, nonprofits, and small businesses build stronger financial futures.",
    founder: { "@type": "Person", name: "Devyne Harris" },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Indianapolis",
      addressRegion: "IN",
      addressCountry: "US",
    },
    areaServed: { "@type": "State", name: "Indiana" },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-317-766-1549",
      email: "info@thechhq.com",
      contactType: "customer service",
      areaServed: "US",
    },
    sameAs: [
      "https://www.instagram.com/connectherhq",
      "https://www.facebook.com/share/1D3tVGvqez/?mibextid=wwXIfr",
    ],
    ...(aggregate && aggregate.count > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: aggregate.average,
            reviewCount: aggregate.count,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ConnectHER",
    url: "https://thechhq.com",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://thechhq.com/services?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <>
      <JsonLd id="jsonld-org" data={orgSchema} />
      <JsonLd id="jsonld-website" data={websiteSchema} />
      <Hero />
      <TrustStrip />
      <Welcome />
      <WhyChoose />
      <Process />
      <ServicesShowcase />
      <FounderMessage />
      <Testimonials reviews={reviews} />
      <LeadMagnet />
      <CTA />
    </>
  );
}
